<script lang="ts">
import { computed, defineAsyncComponent, defineComponent, ref, toRef, watch, type PropType } from 'vue';
import { storeToRefs } from 'pinia';

import PlayDesktop from './PlayDesktop.vue';
import PlayMobile from './PlayMobile.vue';
import DiagnosticExportButton from '../components/DiagnosticExportButton.vue';
import GameLogDownloadButton from '../components/GameLogDownloadButton.vue';
import GameLogCopyButton from '../components/GameLogCopyButton.vue';
import ViewLoadoutButton from '../components/ViewLoadoutButton.vue';
import WaitingForPlayersPanel from '../components/WaitingForPlayersPanel.vue';
import BattlePlanPanel from '../components/BattlePlanPanel.vue';
import BattleBrief from '../components/play/BattleBrief.vue';
import HollowEffectsPanel from '../components/play/HollowEffectsPanel.vue';
import DeckProbabilityPanel from '../components/play/DeckProbabilityPanel.vue';
import AudioControls from '../components/play/AudioControls.vue';
import VfxOverlay from '../components/play/VfxOverlay.vue';
import PlaymatBackground from '../components/play/PlaymatBackground.vue';
import BotAllyStallBanner from '../components/BotAllyStallBanner.vue';
import UpdateAvailableBanner from '../components/UpdateAvailableBanner.vue';
import OutdatedMatchNotice from '../components/OutdatedMatchNotice.vue';
import EndgameActions from '../components/play/EndgameActions.vue';
import FinalTurnBanner from '../components/hud/FinalTurnBanner.vue';
import { useViewport } from '../composables/useViewport';
import { useSkinApplier } from '../composables/useSkinApplier';
import {
  useCompetitiveSubmitOnGameover,
  type SubmissionStatus,
} from '../composables/useCompetitiveSubmitOnGameover';
import { useSeatIdentitiesOnGameover } from '../composables/useSeatIdentitiesOnGameover';
import { useCardImagePrefetch } from '../composables/useCardImagePrefetch';
import { useSoundEffects } from '../composables/useSoundEffects';
import { useComboCue } from '../composables/useComboCue';
import { useComboVfx } from '../composables/useComboVfx';
import { useStrikeBlockedVfx } from '../composables/useStrikeBlockedVfx';
import { useWoundVfx } from '../composables/useWoundVfx';
import { useTransformVfx } from '../composables/useTransformVfx';
import { useExcessiveViolenceVfx } from '../composables/useExcessiveViolenceVfx';
import { useVillainSlashVfx } from '../composables/useVillainSlashVfx';
import { useMastermindHitVfx } from '../composables/useMastermindHitVfx';
import { useVictoryFinaleVfx } from '../composables/useVictoryFinaleVfx';
import { useWoundCue } from '../composables/useWoundCue';
import { useAdaptiveMusic } from '../composables/useAdaptiveMusic';
import { useBotAllyStatus } from '../composables/useBotAllyStatus';
import { useDeployVersionCheck } from '../composables/useDeployVersionCheck';
import { useOperatorEligibility } from '../composables/useOperatorEligibility';
import { useUiStateStore } from '../stores/uiState';
import { useAuthStore } from '../stores/auth';
import { useBuildProvenanceStore } from '../stores/buildProvenance';
import { useConnectionStore } from '../stores/connection';
import { serverUrl } from '../lobby/lobbyApi';
import { readMatchSetup, readBotAllySetup } from '../diagnostics/matchSetupSession';
import {
  launchMatchFromComposition,
  launchBotAllyFromComposition,
} from '../lobby/useCreateMatchFromComposition';
import type { SubmitMove } from '../components/play/uiMoveName.types';
import type { MatchSetupConfig } from '@legendary-arena/game-engine';

// why: WP-804 / D-24689 §6 — the operator board panel is lazy: normal play never
// downloads it, because PlayViewport ships in the entry chunk (App.vue imports it
// statically). It is registered under the PascalCase name and rendered with the
// PascalCase tag so the build keeps it in its own chunk. No errorComponent: if
// the chunk fails to load, the panel renders nothing and Vue logs the error,
// which is acceptable for operator tooling.
const OperatorBoardPanel = defineAsyncComponent(
  () => import('../components/play/OperatorBoardPanel.vue'),
);

// why: WP-339 — the user-facing message for each post-match submission status.
// Mounted once here at the shared viewport root (which holds matchId, D-16501), so
// a single submit covers both the <PlayDesktop> and <PlayMobile> surfaces.
// why: WP-465 — typed `Record<Exclude<SubmissionStatus, 'idle'>, string>` so every
// non-idle status is compile-forced to have copy (a new status without a message is a
// vue-tsc error). `'idle'` is excluded because the banner is hidden while idle
// (v-if="submissionStatus !== 'idle'"), so it intentionally has no message.
const SUBMISSION_MESSAGES: Record<Exclude<SubmissionStatus, 'idle'>, string> = {
  submitting: 'Submitting your score…',
  submitted: 'Score submitted to the leaderboard.',
  already: 'Your score for this match was already submitted.',
  guest: 'Sign in to submit your score to the leaderboard.',
  // why: D-24630 — not rendered as a status line (the in-card prompt covers it),
  // but the Record is exhaustive, and EndgameActions reads the variant.
  'session-expired': 'Your sign-in expired — sign in to save this match.',
  // why: WP-465 — `par_not_published`: the match is not a ranked-gauntlet loadout, so
  // it is permanently not eligible to be scored. An honest, non-alarming line (not an
  // error — the player did nothing wrong).
  // why: the score report card renders only for a scored match (EndgameSummary gates on
  // competitiveScore), so say where it lives. The AI Coach is no longer named here:
  // WP-752 / D-24576 brings it to unscored matches (the casual coach panel below).
  ineligible:
    'This match isn’t part of a ranked gauntlet, so it isn’t scored to the leaderboard. The score report card is available on ranked-gauntlet loadouts.',
  // why: WP-465 — retry-NEUTRAL. The failed bucket mixes transient (network, 500) and
  // permanent (not_owner, visibility_not_eligible, replay_verification_failed) reasons,
  // so the old “It may still be counted shortly.” was a false promise for the permanent
  // ones; this line is honest for both without implying a retry that does not happen.
  failed: 'Couldn’t submit your score to the leaderboard.',
  // why: WP-502 / D-24306 — an early-ended match is never scored, whatever its
  // loadout (a ranked-gauntlet match included), so it gets its own honest line rather
  // than the par_not_published `ineligible` copy, which would be false for a gauntlet.
  'ended-early': 'This match ended early, so it isn’t scored to the leaderboard.',
  // why: WP-804 / D-24689 §3 — an admin-created operator match is never scored;
  // every seat is told so instead of seeing a failed submit.
  'operator-match': 'Operator match — not scored.',
};

/**
 * The match id to coach as a casual (unscored) match, or `null` when this match
 * gets no casual coach panel (WP-752 / D-24576).
 *
 * @param matchId The live match id (`''` when there is no live match).
 * @param submissionStatus The post-match submission status.
 * @returns `matchId` when the match is signed-in and permanently unscored; else `null`.
 */
export function computeCasualCoachMatchId(
  matchId: string,
  submissionStatus: SubmissionStatus,
): string | null {
  // why: `ineligible` is the only permanent, non-scoring status for a signed-in
  // player (`failed` may still be a transient error; `submitting` has not settled;
  // `submitted` / `already` are scored and use the replay-hash coach). Waiting for it
  // honors D-24576's cache order: casual coaching is requested only once submit has
  // settled as permanently unscored, so a casual report is never cached for a match
  // about to be scored. Early ends, including guests', are `'ended-early'` (D-24306,
  // #2312), and guests are `'guest'`, so the status alone excludes both.
  if (matchId === '' || submissionStatus !== 'ineligible') {
    return null;
  }
  return matchId;
}

/**
 * Viewport discriminator. Renders `<PlayDesktop>` when the viewport is
 * desktop-sized; `<PlayMobile>` when the viewport is mobile-portrait.
 * The breakpoint is locked at D-12909 (`max-width: 767px`).
 *
 * Filename `PlayViewport.vue` chosen to disambiguate from the deleted
 * WP-100 `components/play/PlayView.vue` per EC-132 §2.
 *
 * Per the EC-132 §2 SFC authoring whitelist: this page uses a composable
 * (`useViewport`) and conditionally renders children, so it MUST use
 * `defineComponent({ setup() { return {...} } })` per P6-30 / P6-46 /
 * D-6512.
 *
 * Per WP-130, `useSkinApplier()` is invoked here against the viewport's
 * root element so the active playmat skin's CSS class propagates to
 * both `<PlayDesktop>` and `<PlayMobile>` (whichever this component
 * renders). Application is exclusive to this root element — never
 * `<body>` or any global document node — so non-Play pages (lobby,
 * replay-viewer, future routes) never inherit skin styling.
 *
 * @see WP-129 §Acceptance Criteria — viewport discriminator
 * @see WP-130 §D "Skin application"
 * @see DECISIONS.md D-12909 viewport breakpoint
 */
export default defineComponent({
  name: 'PlayViewport',
  components: { PlayDesktop, PlayMobile, DiagnosticExportButton, GameLogDownloadButton, GameLogCopyButton, ViewLoadoutButton, WaitingForPlayersPanel, BattlePlanPanel, BattleBrief, HollowEffectsPanel, DeckProbabilityPanel, AudioControls, VfxOverlay, PlaymatBackground, BotAllyStallBanner, UpdateAvailableBanner, OutdatedMatchNotice, EndgameActions, FinalTurnBanner, OperatorBoardPanel },
  props: {
    submitMove: {
      type: Function as PropType<SubmitMove>,
      required: true,
    },
    // why: forwarded to <PlayDesktop> only (the autoplay bar is desktop-only);
    // part of the additive App.vue → PlayViewport.vue → PlayDesktop.vue
    // matchId prop-drill (D-16501). Defaults to '' so non-live mounts forward
    // an empty value that probes nothing.
    matchId: {
      type: String,
      default: '',
    },
    // why: D-24630 — this viewer's bgio seat id (from the live `?player=` param),
    // so the gameover submit can read the public seat roster and tell an expired
    // sign-in on an account-bound seat apart from a true guest seat. Defaults to
    // '' on non-live mounts, which the submit composable treats as "unknown".
    playerId: {
      type: String,
      default: '',
    },
    villainGroupIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    henchmanGroupIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    heroDeckIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  setup(props) {
    const { isMobile } = useViewport();
    const viewportRoot = ref<HTMLElement | null>(null);
    useSkinApplier(viewportRoot);

    // why: WP-410 — warm the match's card images into the browser cache at match
    // start (from the engine-projected UIState.matchCardImageUrls) so a card paints
    // from cache on reveal instead of round-tripping to R2 mid-turn. Fire-and-forget
    // and a no-op until the manifest arrives, so it is safe at this shared root.
    useCardImagePrefetch();

    // why: WP-412 — mount the notable-event SFX consumer at this shared composable
    // root, reading the SAME useUiStateStore snapshot PlayDesktop's overlay reads
    // (a sibling reader of the store, not a prop consumer). Audio is pure client
    // presentation — it reads UIState only, never writes G/ctx, and adds zero
    // determinism/replay footprint. AudioControls (rendered below) owns the
    // autoplay-unlock arm and the persistent mute/volume UI.
    const audioStore = useUiStateStore();
    const { snapshot: audioSnapshot } = storeToRefs(audioStore);
    useSoundEffects(audioSnapshot);

    // why: WP-413 — the tiered combo cue, mounted at the SAME shared composable
    // root beside useSoundEffects, reading the SAME useUiStateStore snapshot. It
    // watches WP-409's UIState.game.lastPlayEffectsFired scalar and plays an
    // escalating sting through the same WP-412 engine (getAudioEngine()), so it
    // inherits that engine's unlock/mute/volume gate. Pure presentation — reads
    // UIState only, never writes G/ctx; no new control (reuses AudioControls).
    useComboCue(audioSnapshot);

    // why: WP-556 — the tiered combo FLASH (the visual twin of useComboCue),
    // mounted at the SAME shared composable root beside useComboCue, reading the
    // SAME useUiStateStore snapshot. It watches WP-409's
    // UIState.game.lastPlayEffectsFired scalar through the SAME comboTierForCount
    // and emits a combo-flash signal the <VfxOverlay> below renders, so the flash
    // and the sting peak together. Pure presentation — reads UIState only, never
    // writes G/ctx, absent from the determinism hash.
    useComboVfx(audioSnapshot);

    // why: WP-647 (01.5 runtime wiring) — the shield-block VFX consumer, mounted
    // at the SAME shared composable root beside useComboVfx, reading the SAME
    // useUiStateStore snapshot. It keeps an append-only cursor over
    // UIState.notableEvents and emits a shield-block signal the <VfxOverlay> below
    // renders as a Cap-shield beat per strikeBlocked event. Pure presentation —
    // reads UIState only, never writes G/ctx, absent from the determinism hash.
    useStrikeBlockedVfx(audioSnapshot);

    // why: WP-650 (01.5 runtime wiring) — the "wound gained" damage vignette + its
    // audio thud, mounted at the SAME shared composable root beside the other feel
    // consumers, reading the SAME useUiStateStore snapshot. Both watch the local
    // seat's UIState.players[own].woundCount and fire on an INCREASE (a heal
    // decrements it and must not fire): useWoundVfx flashes a red vignette the
    // <VfxOverlay> renders, useWoundCue plays the thud. Pure presentation — reads
    // UIState only, never writes G/ctx, absent from the determinism hash.
    useWoundVfx(audioSnapshot);
    useWoundCue(audioSnapshot);

    // why: WP-672 — the transform beat (a Hero base card powering up into its second
    // form), mounted at the SAME shared composable root beside the other notable-event
    // feel consumers, reading the SAME useUiStateStore snapshot. It keeps an
    // append-only cursor over UIState.notableEvents and emits a transform signal the
    // <VfxOverlay> renders as a gamma-green surge + "TRANSFORMED!" word. Pure
    // presentation — reads UIState only, never writes G/ctx, absent from the hash.
    useTransformVfx(audioSnapshot);

    // why: WP-746 — the Excessive Violence fire beat (a Fight "using Excessive
    // Violence" firing its enrolled EV abilities), mounted at the SAME shared
    // composable root beside the other notable-event feel consumers, reading the
    // SAME useUiStateStore snapshot. It keeps an append-only cursor over
    // UIState.notableEvents and emits a signal the <VfxOverlay> renders as a
    // crimson/steel crossed-swords slash-burst + "EXCESSIVE VIOLENCE!" word. Pure
    // presentation — reads UIState only, never writes G/ctx, absent from the hash.
    useExcessiveViolenceVfx(audioSnapshot);

    // why: WP-755 — the villain-slash beat (a villain or henchman defeated in the
    // City), mounted at the SAME shared composable root and reading the SAME
    // useUiStateStore snapshot; an unmounted producer never fires. It keeps an
    // append-only cursor over UIState.notableEvents and emits a signal the
    // <VfxOverlay> renders as the card slice + takedown word. Mounted IMMEDIATELY
    // after useExcessiveViolenceVfx so, on an Excessive Violence fight (both events
    // in one frame), the "EXCESSIVE VIOLENCE!" word lands in the slot first.
    useVillainSlashVfx(audioSnapshot);

    // why: the mastermind-hit beat + the heroes-win victory finale, mounted at the
    // SAME shared composable root beside the other feel consumers, reading the SAME
    // useUiStateStore snapshot. useMastermindHitVfx watches the shared
    // UIState.mastermind.tacticsDefeated count and fires an escalating ember beat on
    // each Tactic defeat (hit 1..4); useVictoryFinaleVfx fires once on a projected
    // heroes-win (forward-compatible with Final Blow: it lands on the winning fight
    // whichever it is). Both render through <VfxOverlay>. Pure presentation — read
    // UIState only, never write G/ctx, absent from the determinism hash.
    useMastermindHitVfx(audioSnapshot);
    useVictoryFinaleVfx(audioSnapshot);

    // why: WP-560 — the adaptive danger score, mounted at the SAME shared
    // composable root beside useComboCue and reading the SAME snapshot. One
    // host covers both the desktop and mobile play surfaces.
    useAdaptiveMusic(audioSnapshot);

    // why: WP-339 — on gameover, submit this match's competitive score once (for
    // an authenticated player). toRef keeps matchId reactive so the composable
    // re-arms if a new live match reuses this viewport instance.
    // why: INFRA (endgame seat names) — on gameover, fetch the per-seat identity
    // roster so the co-op VP recap can label seats "Player N (@handle)" / "(Bot)".
    // The competitive report card gets the same roster from its submit response;
    // this covers the unscored / guest / local matches that fall through to the
    // recap. Public read (runs for guests too); toRef keeps matchId reactive so it
    // re-arms for a new live match on the same viewport instance. Created before
    // the submit hook so it can feed that hook's expired-session check (D-24630).
    const { seatIdentities } = useSeatIdentitiesOnGameover(
      toRef(props, 'matchId'),
    );
    // why: D-24630 — the roster + this seat's id let the submit hook tell an
    // expired sign-in on an account-bound seat from a true guest seat.
    const { submissionStatus, submittedScore } = useCompetitiveSubmitOnGameover(
      toRef(props, 'matchId'),
      { seatIdentities, playerId: toRef(props, 'playerId') },
    );
    const submissionMessage = computed<string>(() => {
      const status = submissionStatus.value;
      // why: WP-465 — 'idle' has no status line and no message entry; narrowing it
      // out lets SUBMISSION_MESSAGES be the exhaustive
      // Record<Exclude<SubmissionStatus, 'idle'>, string> (a missing status is a
      // compile error, not a silent ''). WP-339 — 'guest' also renders no status
      // line: guests get the richer in-card sign-in prompt (EndgameSummary
      // showGuestSignIn), so a second sign-in message would be redundant.
      // D-24630 — 'session-expired' likewise gets the in-card sign-in-to-save prompt.
      if (status === 'idle' || status === 'guest' || status === 'session-expired') {
        return '';
      }
      return SUBMISSION_MESSAGES[status];
    });

    // why: a guest (no account) is never submitted, so submissionStatus latches to
    // 'guest'. EndgameSummary uses this to render a sign-in conversion prompt in the
    // otherwise-empty competitive-score slot, instead of leaving dead space.
    const isGuestResult = computed<boolean>(() => submissionStatus.value === 'guest');
    // why: D-24630 — an account-bound seat whose sign-in expired is NOT a guest:
    // the match can still be saved after re-login, so EndgameSummary shows the
    // "sign-in expired — sign in to save this match" prompt instead of guest copy.
    const isSessionExpiredResult = computed<boolean>(
      () => submissionStatus.value === 'session-expired',
    );

    // why: WP-752 — computed once here (the only place that holds both matchId and
    // submissionStatus) and passed down to both play surfaces, which only render it.
    const casualCoachMatchId = computed<string | null>(() =>
      computeCasualCoachMatchId(props.matchId, submissionStatus.value),
    );

    // why: WP-415 — mounted ONCE at this 01.5 play-root host (the WP-410/412
    // precedent), so the bot-ally stall banner covers BOTH the <PlayMobile> and
    // <PlayDesktop> surfaces. Probes WP-414's status surface once; a non-bot-ally
    // match reports `absent` and the poll stops immediately, so this adds nothing
    // outside a real bot-ally match. Pure client presentation — HTTP-only, no
    // engine/registry import, no G/ctx read.
    const { hasStopped: isBotAllyStopped, message: botAllyMessage } =
      useBotAllyStatus(props.matchId);

    // why: WP-418 — mounted ONCE at this 01.5 play-root host (the WP-410/412/415
    // wiring precedent), so the deploy-refresh banner covers BOTH the <PlayMobile>
    // and <PlayDesktop> surfaces. Detects a newer client build (a mid-match deploy
    // swaps hashed chunks and freezes the stale tab) and offers a reload — the one
    // recovery the reconnect/resync stack cannot do, since it re-anchors match
    // state but never the JS bundle (D-24238). Pure client presentation — no
    // engine/registry import, no G/ctx read; fail-soft, non-move-gating.
    const { updateAvailable: isUpdateAvailable } = useDeployVersionCheck();

    // why: the diagnostics and game-log exports stamp which client build, server
    // build and match-creation time produced a match (a match keeps the card rules
    // built at its setup, and the client and server deploy independently). Loaded
    // once per match id here at the play root so the export click handlers read it
    // synchronously. Fail-soft: a failed probe leaves the exports unchanged.
    const buildProvenanceStore = useBuildProvenanceStore();
    const clientBuild = { gitSha: __GIT_SHA__, buildTimestamp: __BUILD_TIMESTAMP__ };
    watch(
      () => props.matchId,
      (matchId) => {
        void buildProvenanceStore.load(serverUrl, matchId, clientBuild, (url) => fetch(url));
      },
      { immediate: true },
    );
    // why: a server deploy drops every socket and the client reconnects to the new
    // build. Re-read the server build after each reconnect, or the exports keep
    // naming the build the match started on and miss that the match now predates
    // the running server (match NVDdebFa9eA, 2026-10-26: created 22:48 on
    // 72231815, server restarted as 1c037ce4 at 22:49, export still said 72231815).
    const connectionStore = useConnectionStore();
    watch(
      () => connectionStore.transportReconnectCount,
      () => {
        void buildProvenanceStore.refresh(serverUrl, props.matchId, clientBuild, (url) => fetch(url));
      },
    );

    // why: the reload is USER-INITIATED (a button), never automatic — a forced
    // reload mid-turn would discard an in-progress action and read as hostile
    // (WP-418 §Out of scope). The reload site is owned here at the host and
    // prop-drilled into the pure banner (the ConnectionStatusBanner/resync pattern).
    function reloadForUpdate(): void {
      window.location.reload();
    }

    // why: the stall escape is a NON-DESTRUCTIVE, client-only navigation — it
    // returns to the lobby (the default route, reached by clearing the live
    // `?match=` params) and calls NO server endpoint; the server-side match is
    // never abandoned from here.
    function returnToLobby(): void {
      window.location.assign('/');
    }

    // why: WP-502 / D-24306 — the post-match "Play Again" / "Back to Lobby" panel
    // is mounted ONCE here at the shared viewport root (the WP-410/412/415/418
    // wiring precedent), so it covers BOTH the <PlayMobile> and <PlayDesktop>
    // surfaces. It reads the SAME useUiStateStore snapshot the submit hook reads;
    // gameOver presence is the "match over" signal. Pure client — no engine/G write.
    const endgameStore = useUiStateStore();
    const { snapshot: endgameSnapshot } = storeToRefs(endgameStore);
    const authStore = useAuthStore();

    // why: WP-654 — the final-turn warning banner reads the SAME useUiStateStore
    // snapshot the endgame/submit hooks read (a sibling store reader, not a prop
    // consumer), mounted ONCE at this shared viewport root so it covers BOTH
    // <PlayDesktop> and <PlayMobile> (the WP-410/412/415/418 single-host
    // precedent). The engine projects finalTurn only during the deck-exhaustion
    // final turn and omits it once gameOver is set, so this computed is the
    // banner's whole gate — no client-side "is it the final turn" logic here.
    const finalTurn = computed(() => endgameSnapshot.value?.finalTurn);

    // why: WP-804 — an operator match (UIState.isDebugMatch, public to every
    // audience) shows every seat the "not scored" badge.
    const isOperatorMatch = computed<boolean>(
      () => endgameSnapshot.value?.isDebugMatch === true,
    );
    // why: WP-804 — the play surface probes eligibility only in an operator
    // match, so a normal game never costs the server an admin lookup.
    const { isOperator } = useOperatorEligibility(isOperatorMatch);
    // why: WP-804 — the panel needs all three: an operator match, an eligible
    // (admin) viewer, and a live match id to read (the fixture route has '').
    const isOperatorPanelVisible = computed<boolean>(
      () => isOperatorMatch.value && isOperator.value && props.matchId !== '',
    );

    const isGameOver = computed<boolean>(
      () => endgameSnapshot.value?.gameOver !== undefined,
    );
    const isEndedEarly = computed<boolean>(
      () => endgameSnapshot.value?.gameOver?.endedEarly === true,
    );

    // why: a match created before the running server booted keeps the card rules
    // of its setup build (hero hooks are built once at Game.setup), so a player
    // verifying a just-shipped rule in it sees the old behaviour. This reads the
    // SAME build-provenance flag the game-log header and diagnostics export carry
    // (loaded above, refreshed after each reconnect, so a mid-match server deploy
    // raises it too). Hidden at game over, where Play Again already starts fresh.
    const isOutdatedMatchNoticeShown = computed<boolean>(
      () => buildProvenanceStore.provenance?.isMatchOlderThanServer === true && !isGameOver.value,
    );
    const outdatedMatchCreatedAtIso = computed<string | null>(
      () => buildProvenanceStore.provenance?.matchCreatedAtIso ?? null,
    );
    const outdatedServerBootedAtIso = computed<string | null>(
      () => buildProvenanceStore.provenance?.serverBootedAtIso ?? null,
    );
    // why: the loadout this match launched with, stashed client-side at create
    // (matchSetupSession). Null when the viewer joined a match they did not create
    // or the tab's session cleared — Play Again then hides (only Back to Lobby).
    const stashedConfig = computed<MatchSetupConfig | null>(() => {
      const raw = readMatchSetup(props.matchId);
      return raw !== null ? (raw as MatchSetupConfig) : null;
    });
    // why: the composition is player-count-specific (WP-370), so Play Again MUST
    // reuse the ORIGINAL seat count — derived from the projected seat list, the
    // server-authoritative count of players in this match.
    const seatCount = computed<number>(
      () => endgameSnapshot.value?.players?.length ?? 0,
    );
    // why: Play Again needs an authenticated account (create requires auth,
    // D-24092) AND a stashed loadout AND a valid seat count. A guest or a
    // no-loadout viewer sees only Back to Lobby.
    const canPlayAgain = computed<boolean>(
      () =>
        isGameOver.value &&
        authStore.token !== null &&
        stashedConfig.value !== null &&
        seatCount.value >= 1,
    );

    const isRelaunching = ref(false);
    const playAgainError = ref('');

    /**
     * Relaunches a fresh match with this match's loadout (same composition, same
     * seat count) and drops the player into seat 0. On success
     * launchMatchFromComposition navigates the tab (window.location.search), so
     * this component unmounts; only a failure returns here to surface a message.
     */
    async function playAgain(): Promise<void> {
      const config = stashedConfig.value;
      const authToken = authStore.token;
      if (config === null || authToken === null || isRelaunching.value) {
        return;
      }
      isRelaunching.value = true;
      playAgainError.value = '';
      // why: WP-502 Play Again fix — if THIS match was created with a bot ally
      // (the bot parameters were stashed at create time), rebuild a bot-ally
      // match so the bot seat is filled again; otherwise relaunching via the
      // plain create path would leave the bot's seat empty ("waiting for a
      // friend"). A plain human match has no stash and takes the plain path.
      const botAlly = readBotAllySetup(props.matchId);
      // why: the join seat label is cosmetic; neither the auth store nor the
      // UIState projection carries a display name, so a stable default is used.
      const result =
        botAlly !== null
          ? await launchBotAllyFromComposition({
              config,
              playerCount: seatCount.value,
              botCount: botAlly.botCount,
              policy: botAlly.policy,
              playerName: 'Player',
              authToken,
            })
          : await launchMatchFromComposition({
              config,
              playerCount: seatCount.value,
              playerName: 'Player',
              authToken,
            });
      if (!result.ok) {
        playAgainError.value = result.message;
        isRelaunching.value = false;
      }
    }

    return {
      isMobile,
      viewportRoot,
      matchId: props.matchId,
      villainGroupIds: props.villainGroupIds,
      henchmanGroupIds: props.henchmanGroupIds,
      heroDeckIds: props.heroDeckIds,
      submissionStatus,
      submittedScore,
      seatIdentities,
      submissionMessage,
      isGuestResult,
      isSessionExpiredResult,
      casualCoachMatchId,
      isBotAllyStopped,
      botAllyMessage,
      returnToLobby,
      isUpdateAvailable,
      reloadForUpdate,
      isOutdatedMatchNoticeShown,
      outdatedMatchCreatedAtIso,
      outdatedServerBootedAtIso,
      isGameOver,
      isEndedEarly,
      canPlayAgain,
      isRelaunching,
      playAgainError,
      playAgain,
      finalTurn,
      isOperatorMatch,
      isOperatorPanelVisible,
    };
  },
});
</script>

<template>
  <div ref="viewportRoot" class="play-viewport">
    <!--
      // why: WP-666 — the fixed full-bleed playmat background layer, mounted
      // ONCE here at the shared viewport root (the <VfxOverlay> single-host
      // precedent) so the selected skin's mat art paints behind BOTH the
      // <PlayMobile> and <PlayDesktop> surfaces. useSkinApplier (invoked above
      // on this same root) sets the --skin-board-image variable the layer reads;
      // the layer is fixed + pointer-events:none + z-index:-1, so it never
      // disturbs the display:contents root or intercepts a click.
    -->
    <PlaymatBackground />
    <!--
      // why: the older-rules notice, mounted once here so it covers BOTH play
      // surfaces. Self-hides unless the match predates the running server. On
      // phones it is the column's first row; on desktop it is a fixed bar over the
      // brand header (its own CSS), dropped below the update banner when both show.
    -->
    <OutdatedMatchNotice
      :class="{ 'outdated-match-notice--below-update-banner': isUpdateAvailable }"
      :is-match-older-than-server="isOutdatedMatchNoticeShown"
      :match-created-at-iso="outdatedMatchCreatedAtIso"
      :server-booted-at-iso="outdatedServerBootedAtIso"
      :start-new-match="returnToLobby"
    />
    <PlayMobile
      v-if="isMobile"
      :submit-move="submitMove"
      :villain-group-ids="villainGroupIds"
      :henchman-group-ids="henchmanGroupIds"
      :hero-deck-ids="heroDeckIds"
      :competitive-score="submittedScore"
      :seat-identities="seatIdentities"
      :show-guest-sign-in="isGuestResult"
      :show-session-expired-sign-in="isSessionExpiredResult"
      :casual-coach-match-id="casualCoachMatchId"
    />
    <PlayDesktop
      v-else
      :submit-move="submitMove"
      :match-id="matchId"
      :villain-group-ids="villainGroupIds"
      :henchman-group-ids="henchmanGroupIds"
      :hero-deck-ids="heroDeckIds"
      :competitive-score="submittedScore"
      :seat-identities="seatIdentities"
      :show-guest-sign-in="isGuestResult"
      :show-session-expired-sign-in="isSessionExpiredResult"
      :casual-coach-match-id="casualCoachMatchId"
    />
    <!--
      // why: D-24650 — the three always-on bottom-left utilities (Download
      // diagnostics, View loadout, Deck odds) share ONE fixed dock instead of each
      // pinning its own corner offset. Stacked vertically at 8 / 40 / 72px they rose
      // ~116px above the window bottom, past the ~55px footer band, and covered the
      // turn bar's "Reveal top of Villain Deck" button on desktop: a Reveal click
      // opened the loadout tab instead, and a blocked pop-up's message then hid
      // Reveal entirely. On desktop the dock is one row inside the footer band; on
      // phones (the scrolling PlayMobile column) it keeps the old vertical stack.
    -->
    <div class="play-utility-dock" data-testid="play-utility-dock">
      <DiagnosticExportButton />
      <ViewLoadoutButton />
      <DeckProbabilityPanel />
    </div>
    <!--
      // why (Jeff feedback): the endgame outcome screen collapses the board and its
      // GameLogPanel Save button behind the "View final board" toggle, leaving the
      // game-log transcript with no reachable download there. This button (self-
      // sourced from the UIState store, shown only at game over) sits beside
      // "Download diagnostics" so the log .txt can be saved from the outcome screen.
    -->
    <GameLogDownloadButton />
    <!--
      // why (WP-707, Jeff feedback): the endgame Copy affordance. The in-match
      // GameLogPanel Copy button is collapsed behind "View final board" at game
      // over, so this clipboard sibling (self-sourced from the UIState store,
      // shown only at game over) stacks above "Download game log" so the log
      // transcript can be copied without expanding the board.
    -->
    <GameLogCopyButton />
    <!--
      // why: WP-363 — <ViewLoadoutButton> is mounted ONCE at the shared viewport
      // root, inside the utility dock above, so the in-match "View loadout in
      // Registry Viewer" link covers BOTH the <PlayMobile> and <PlayDesktop>
      // surfaces. Self-hides when there is no `?match=` (not a live match).
    -->
    <!--
      // why: WP-369 — mounted ONCE here at the shared viewport root (the same
      // shared-child case as <ViewLoadoutButton>), so the pre-match "Waiting for
      // players" panel covers BOTH the <PlayMobile> and <PlayDesktop> surfaces.
      // Supersedes WP-366's always-on corner InviteFriendControl: it renders only
      // while the match has an open seat (polled from the public lobby list) and
      // auto-hides when full. It reads only `?match=` — never G or the UIState
      // projection.
    -->
    <WaitingForPlayersPanel />
    <!--
      // why: WP-637 — mounted ONCE at the shared viewport root so the Battle Plan
      // panel covers BOTH the <PlayMobile> and <PlayDesktop> surfaces (the
      // WP-363/WP-502 overlay precedent). It self-sources matchId from `?match=`
      // and self-hides when there is no live match, so it adds no DOM outside real
      // play. It reads the UIState snapshot only for the D-24450 lifecycle gating
      // (which phase is editable) — never writes G/ctx/UIState, never submitMove.
    -->
    <BattlePlanPanel class="play-battle-plan" />
    <!--
      // why: WP-786 / D-24634 — mounted ONCE at the shared viewport root so the
      // battle brief covers BOTH <PlayMobile> and <PlayDesktop> (PlayMobile has no
      // matchId, D-16501). It renders only in the engine's lobby phase and sends
      // only setPlayerReady, then startMatchIfReady after the next server frame.
    -->
    <BattleBrief :submit-move="submitMove" :match-id="matchId" />
    <!--
      // why: WP-258 — mounted ONCE here at the shared viewport root (the
      // Mounting Rule's shared-child case), alongside <DiagnosticExportButton>,
      // so a single fixed-position overlay covers BOTH the <PlayMobile> and
      // <PlayDesktop> production play surfaces (whichever this viewport renders).
      // The panel self-hides (v-if ≥1 record) so it adds no DOM during a clean
      // match.
    -->
    <HollowEffectsPanel />
    <!--
      // why: WP-804 / D-24689 §3 — the public operator-match badge lives in
      // <TopHudBar>'s flow (both play surfaces), not as a fixed chip here.
    -->
    <!--
      // why: WP-804 / D-24689 §6 — the lazy operator board panel, mounted ONCE
      // here at the shared viewport root so it covers both play surfaces. Shown
      // only in an operator match, to an eligible admin, with a live match id.
    -->
    <OperatorBoardPanel
      v-if="isOperatorPanelVisible"
      :match-id="matchId"
      :player-id="playerId"
      :submit-move="submitMove"
    />
    <!--
      // why: WP-607 — <DeckProbabilityPanel> is mounted ONCE at the shared viewport
      // root, inside the utility dock above, so the collapsible plain card counter
      // covers BOTH the <PlayMobile> and <PlayDesktop> surfaces. Self-hides (v-if
      // hasData); presentational only (reads the UIState store).
    -->
    <!--
      // why: WP-412 — mounted ONCE here at the shared viewport root (the single
      // 01.5 wiring host), so the fixed-position mute/volume control + the
      // autoplay-unlock arm cover BOTH the <PlayMobile> and <PlayDesktop>
      // surfaces. Self-contained (position: fixed via its own scoped CSS), so no
      // layout prop chain and no second wiring host.
    -->
    <AudioControls />
    <!--
      // why: WP-556 — the single full-bleed VFX overlay, mounted ONCE here at the
      // shared viewport root beside <AudioControls> (the same fixed-overlay
      // precedent), so the combo flash + synergy call-out cover BOTH the
      // <PlayMobile> and <PlayDesktop> surfaces from one host. pointer-events:none
      // + position:fixed via its own scoped CSS, so no layout prop chain and no
      // second wiring host. It renders the useComboVfx signal mounted above.
    -->
    <VfxOverlay />
    <!--
      // why: WP-654 — the final-turn warning banner, mounted ONCE here at the
      // shared viewport root (the WP-410/412/415/418 single-host precedent), so
      // it covers BOTH the <PlayMobile> and <PlayDesktop> surfaces. WP-368
      // mounted it in <ArenaHud>, which the app renders only on the dev
      // ?fixture= route — never in a real match — so the banner never appeared
      // in play (D-24465). The pure component self-hides via its own v-if on the
      // finalTurn prop; the PlayViewport-scoped `final-turn-banner-overlay` class
      // (applied to the child's root) positions it as a fixed top-center overlay
      // without touching the component.
    -->
    <FinalTurnBanner :final-turn="finalTurn" class="final-turn-banner-overlay" />
    <!--
      // why: WP-415 — mounted ONCE here at the shared viewport root (the 01.5
      // wiring host), so the co-op stall banner covers BOTH the <PlayMobile> and
      // <PlayDesktop> surfaces. Self-hides unless the bot ally stopped abnormally
      // (v-if hasStopped inside the component), so it adds no DOM during a healthy
      // match or a normal end. Escape is a client-only Return-to-lobby navigation.
    -->
    <BotAllyStallBanner
      :has-stopped="isBotAllyStopped"
      :message="botAllyMessage"
      :return-to-lobby="returnToLobby"
    />
    <!--
      // why: WP-418 — mounted ONCE here at the shared viewport root (the 01.5
      // wiring host, beside the other banners), so the deploy-refresh notice
      // covers BOTH the <PlayMobile> and <PlayDesktop> surfaces. Self-hides until
      // a newer build is detected (v-if inside the component), so it adds no DOM
      // during a normal session. The reload is user-initiated (never mid-turn
      // auto-reload); a failed version fetch shows nothing (fail-soft).
    -->
    <UpdateAvailableBanner
      :update-available="isUpdateAvailable"
      :refresh="reloadForUpdate"
    />
    <!--
      // why: WP-502 / D-24306 — the post-match action panel, mounted ONCE here at
      // the shared viewport root so "Play Again" (relaunch the same loadout) and
      // "Back to Lobby" cover BOTH the <PlayMobile> and <PlayDesktop> surfaces.
      // Self-hides until the match is over (v-if visible inside the component).
    -->
    <EndgameActions
      :visible="isGameOver"
      :ended-early="isEndedEarly"
      :can-play-again="canPlayAgain"
      :is-relaunching="isRelaunching"
      :error-message="playAgainError"
      :status-message="submissionMessage"
      :status-variant="submissionStatus"
      :on-play-again="playAgain"
      :on-return-to-lobby="returnToLobby"
    />
  </div>
</template>

<style scoped>
/* why (WP-666 / D-24482): the viewport root is a real positioned box — NOT the
   former `display: contents` — so it can (a) establish a stacking context that
   scopes the <PlaymatBackground> absolute layer beneath the board content and
   above the opaque `.app-shell` ground, and (b) confine that layer to the play
   area (below the app header). `min-height: 100vh` keeps the mat covering the
   full screen even when the board is shorter than the viewport. The fixed
   overlays (AudioControls / VfxOverlay / banners) are `position: fixed`, so they
   stay viewport-anchored regardless of this box. PlayDesktop / PlayMobile keep
   their own `max-width` + `margin-inline: auto` centering within this block. */
.play-viewport {
  display: block;
  position: relative;
  z-index: 0;
  min-height: 100vh;
}

/* why: WP-688 / D-24502 lock 1 — on DESKTOP the play area fills the flex gap the
   app-shell (a flex column: brand header + flex:1 content + footer) leaves
   between the header and footer, instead of forcing min-height:100vh. Below a
   ~68px header and a ~55px footer, a 100vh play area guaranteed a page scroll no
   matter how short the board — the structural half of Tex's sub-1366 scroll.
   <PlayDesktop> then flex-fills this box and scales its board to fit it (no page
   scroll). Scoped to ≥768px so the D-12909 <PlayMobile> surface keeps its own
   min-height:100vh scrolling-column behavior BYTE-UNCHANGED. All the play-viewport
   overlays (PlaymatBackground, banners, controls) are position:absolute/fixed, so
   switching this box to a flex column only affects the in-flow board child. */
@media (min-width: 768px) {
  .play-viewport {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
  }
}

/* why: D-24650 — the shared bottom-left utility dock. It is the only fixed
   positioning for Download diagnostics / View loadout / Deck odds; the children
   are in-flow inside it. pointer-events: none on the dock (auto on its items)
   so the gaps between items never swallow a click meant for the board.
   z-index matches the former per-button 9999 so the diagnostics stay reachable
   above any stuck overlay. Default (phones, < 768px): the former vertical stack,
   bottom item = Download diagnostics, growing upward. */
.play-utility-dock {
  position: fixed;
  left: 8px;
  bottom: 8px;
  z-index: 9999;
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-start;
  gap: 6px;
  max-width: calc(100vw - 16px);
  pointer-events: none;
}

.play-utility-dock > * {
  pointer-events: auto;
}

/* why: D-24650 — on desktop the board fills the space down to the ~55px footer
   band, so the dock lays its items out as ONE row along the window bottom (the
   footer band, left of the centered copyright) instead of a stack that rises into
   the board's turn bar. align-items: flex-end keeps every collapsed item on the
   bottom line; an expanded Deck odds body grows upward as before. */
@media (min-width: 768px) {
  .play-utility-dock {
    flex-direction: row;
    align-items: flex-end;
    gap: 8px;
  }
}

/* why: desktop — the Battle Plan toggle sits in the brand header's empty band,
   just right of the wordmark (24px padding + a ~137px wordmark at the fixed 22px
   h4 size, so 11.5rem leaves a ~24px gap), vertically centered in the ~68px
   header. Its old top-right corner covered the header nav ("Sign out"), and
   below the header the board's status strip and the operator panel fill the
   width. Phones keep the component's own top-right placement. The extra
   .play-viewport ancestor outranks the component's own scoped .battle-plan rule. */
@media (min-width: 768px) {
  .play-viewport .play-battle-plan {
    top: 1.2rem;
    right: auto;
    left: 11.5rem;
  }

  /* why: the update and outdated-match banners are fixed bars over the header
     (z 50 / 49) that cover the nav links while shown; the toggle drops beneath
     them like the nav does instead of painting over the banner's text. Its
     normal 9999 stays otherwise, so an open plan sits above the Battle Brief. */
  .play-viewport:has(.update-banner, .outdated-match-notice) .play-battle-plan {
    z-index: 48;
  }
}

/* why: WP-654 — position the shared final-turn banner as a fixed top-center
   overlay over the play surface (the banner-overlay precedent set by the other
   viewport-root banners, which own their own fixed positioning). Vue applies
   this component's scope id to the child <FinalTurnBanner>'s root element, so
   this class styles that root without editing the pure component. z-index sits
   above the mat but below the fixed audio/diagnostics controls. */
.final-turn-banner-overlay {
  position: fixed;
  top: 0.75rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 45;
  width: max-content;
  max-width: min(92vw, 40rem);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
}
</style>

<style>
/* why: phones — the fixed audio controls and utility dock cover the bottom ~5rem
   of the viewport, so the brand footer (copyright + build stamp) at the end of
   the scrolling PlayMobile column always sat underneath them. Extra bottom
   padding on the footer, only while a play viewport is mounted, lets it scroll
   clear. Unscoped because the footer belongs to AppShell, not this component. */
@media (max-width: 767px) {
  .app-shell:has(.play-viewport) .brand-footer {
    padding-bottom: 6rem;
  }
}
</style>